/**
 * MVP-272 Phase 0. `Locale` persistence: `localStorage`-with-in-memory-fallback, the exact shape
 * of `vocalLevelsPrefs.ts`/`libraryPrefs.ts` — see either file's own header comment for why
 * (reject-and-default rather than trust-the-stored-blob, a `try/catch` around every storage call
 * for private-mode browsers where `getItem`/`setItem` throw, and correctness under `node --test`,
 * which has no `localStorage` global at all — see `dismissedRequest.test.ts`'s own note on that).
 *
 * Default is `'th'` per the owner's decision (MVP-272): Thai ships as the default, English stays
 * switchable. A first-time visitor, or one whose stored value is missing, corrupted, or from a
 * locale this build no longer supports, gets Thai — never a silent English fallback. `resolveLocale`
 * is the one place that rule lives, so `loadLocale` and any future caller (a settings import, a
 * URL-param override) share exactly one fallback decision.
 */

export type Locale = 'th' | 'en';

export const DEFAULT_LOCALE: Locale = 'th';

/** Exported because a SECOND document reads this preference through the browser's `storage` event
 * rather than through this module's own functions — see `isLocaleStorageEvent` below. */
export const STORAGE_KEY = 'mvcp.locale.v1';

/** Pure — validates a raw value of unknown origin (parsed storage, a future import file, a URL
 * param) against the two locales this build actually ships, falling back to `DEFAULT_LOCALE`
 * rather than trusting anything else through. */
export function resolveLocale(value: unknown): Locale {
  return value === 'th' || value === 'en' ? value : DEFAULT_LOCALE;
}

function hasLocalStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

export function loadLocale(): Locale {
  if (!hasLocalStorage()) return DEFAULT_LOCALE;
  try {
    return resolveLocale(localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_LOCALE; // corrupt/blocked storage is never a crash
  }
}

export function saveLocale(locale: Locale): void {
  if (!hasLocalStorage()) return;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* private-mode/storage-full — losing the preference is fine, throwing is not */
  }
}

/**
 * MVP-287. Pure — decides whether a `storage` event carries a locale change this app should adopt.
 *
 * The Stage is a second document (`useStageWindow`'s `window.open`) with its own `I18nProvider`,
 * so the browser's `storage` event is the ONLY signal it gets when the operator switches language
 * in the cockpit. Without it the audience display keeps the locale it opened with until someone
 * reloads it — and reloading the screen the whole room is watching is a black frame mid-song.
 *
 * `key` is `null` when the whole store was cleared (`localStorage.clear()`), which is a locale
 * change too: the stored preference is gone and `loadLocale()` now answers `DEFAULT_LOCALE`. The
 * caller re-reads through `loadLocale()` rather than trusting `newValue`, so both cases take the
 * same path and the "what does a missing value mean" rule stays in `resolveLocale` alone.
 */
export function isLocaleStorageEvent(key: string | null): boolean {
  return key === null || key === STORAGE_KEY;
}

/** Pure — mirrors `applyDocumentTheme` (`features/ops/theme.ts`): takes the element rather than
 * reading `document` so it is testable with a plain object and no DOM. This is the one place
 * application code ever writes `<html lang>`; `index.html`'s own `lang="th"` is only the
 * pre-hydration guess for the very first paint, before this runs at all. */
export function applyDocumentLang(root: { lang: string }, locale: Locale): void {
  root.lang = locale;
}
